import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRepeat } from '@fortawesome/pro-solid-svg-icons/faRepeat';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';

import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { Button } from 'ui-components/components/button';
import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_ERROR,
} from 'ui-components/services/notification-service';

import { getDecimalsToDisplayFromPrice } from '../../../../../services/decimal-display-service';
import FormattedCurrencyContainer from '../../../../../components/formatted-currency/container';
import { sellerProvidersUpdated } from '../../../../../store/seller/seller.actions';
import { useModal } from '../../../../../hooks/use-modal';
import useWalletAddFunds from '../../hooks/use-wallet-add-funds';
import { AddFundsButtonsWithModal } from '../../components/funding/add-funds-buttons-with-modal';
import { AutoFundingModal } from '../../components/auto-fund-modal/auto-funding-modal';
import useWalletSaveAutoFunding from '../../../providers/add-funds-modal/hooks/use-wallet-save-auto-funding';

import { PageEvents, PageEventSourceStrings, useTrackEvent } from '../../hooks/use-track-event';

import messages from './messages';
import { messages as addFundsMessages } from '../../../../../wallet/messages';

import commonCardStyles from '../cards.module.less';
import balanceCardStyles from '../balance-cards.module.less';
import { WalletRegistrationOrUpdateButton } from '../../../../../wallet/components/wallet-registration-or-update-button';
import { useWallet } from '../../../../../wallet/hooks/use-wallet';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../../../tracking/tracking-constants';
import { usePaymentAndSubscriptionPageContext } from '../../context/payment-and-subscription-page-context';

const showSuccessNotification = () => {
  NotificationService.showNotification({
    notificationType: NOTIFICATION_TYPE_SUCCESS,
    title: <FormattedMessage {...addFundsMessages.addFundsSuccessTitle} />,
    content: <FormattedMessage {...addFundsMessages.addFundsSuccessContent} />,
    duration: NOTIFICATION_DURATIONS.SHORT,
  });
};

const showFailureNotification = () => {
  NotificationService.showNotification({
    notificationType: NOTIFICATION_TYPE_ERROR,
    title: <FormattedMessage {...addFundsMessages.addFundsErrorTitle} />,
    content: <FormattedMessage {...addFundsMessages.addFundsErrorContent} />,
    duration: NOTIFICATION_DURATIONS.SHORT,
  });
};

const sourceString = PageEventSourceStrings.WALLET_BALANCE_CARD;

export const WalletBalanceCard = ({ walletId: walletIdProp }) => {
  const dispatch = useDispatch();
  const { balance, currencyCode, isWalletRegistered, autoFundConfig, walletId } = useWallet(walletIdProp) ?? {};
  const currentBalance = balance?.value ?? 0;
  const sellerCurrencyCode = currencyCode;

  const autoFundEnabled = !!autoFundConfig?.threshold?.value;
  const addFundsToWallet = useWalletAddFunds(walletId);

  const trackFundEvent = useTrackEvent(PageEvents.CONFIRM_FUND_WALLET_CLICK, sourceString);
  const trackAddFunds = useTrackWorkflow(Workflows.ADD_FUNDS_FLOW);
  const onSaveAutoFundConfig = useWalletSaveAutoFunding(walletId);

  const onFundSuccess = useCallback(
    (amount, currency) => {
      dispatch(sellerProvidersUpdated());
      trackAddFunds(WorkflowSteps.COMPLETE, { amount, currency });
      showSuccessNotification();
    },
    [dispatch, trackAddFunds],
  );

  const onFundsError = useCallback(
    (error, amount, currency) => {
      trackAddFunds(WorkflowSteps.ERROR, { error, amount, currency });
      showFailureNotification();
    },
    [trackAddFunds],
  );

  const { open: openAutoFund, isOpen: autoFundModalIsOpen, close: closeAutoFundModal } = useModal();

  const trackAutofundClick = useTrackEvent(PageEvents.MANAGE_AUTOFUND_CONFIG_CLICK, sourceString);
  const openAutoFundModal = useCallback(() => {
    trackAutofundClick();
    openAutoFund();
  }, [openAutoFund, trackAutofundClick]);

  const onAutoFundSaveSuccess = useCallback(() => {
    dispatch(sellerProvidersUpdated());
    closeAutoFundModal();
  }, [closeAutoFundModal, dispatch]);

  const { reloadData } = usePaymentAndSubscriptionPageContext();

  const modalProps = useMemo(
    () => ({
      onClose: reloadData,
    }),
    [reloadData],
  );

  return (
    <>
      <div className={commonCardStyles.body}>
        <div className={commonCardStyles['centered-row']}>
          <span className={balanceCardStyles.title}>
            <FormattedMessage {...messages.walletBalanceHeader} />
          </span>
          <div className={balanceCardStyles.amount}>
            <FormattedCurrencyContainer
              currencyCode={sellerCurrencyCode}
              fractionDigits={getDecimalsToDisplayFromPrice(currentBalance)}
              value={currentBalance}
            />
            {autoFundEnabled && <FontAwesomeIcon className={balanceCardStyles['auto-fund-icon']} icon={faRepeat} />}
          </div>
        </div>
        <div className={commonCardStyles['centered-row']}>
          <div className={balanceCardStyles.subtext}>
            {isWalletRegistered ? (
              <FormattedMessage {...messages.walletCardNote} />
            ) : (
              <FormattedMessage {...messages.setupWalletCardNote} />
            )}
          </div>
          {isWalletRegistered && (
            <Button className={balanceCardStyles.link} onClick={openAutoFundModal} theme='link'>
              {autoFundEnabled ? (
                <FormattedMessage {...messages.walletCardManageAutoFund} />
              ) : (
                <FormattedMessage {...messages.walletCardAutoFundLink} />
              )}
            </Button>
          )}
        </div>
        <div className={balanceCardStyles['add-funds']}>
          <FormattedMessage {...messages.walletBalanceSubTitle} />
        </div>
        {!isWalletRegistered && (
          <div className={balanceCardStyles.banner}>
            <FontAwesomeIcon icon={faInfoCircle} />
            <div>
              <FormattedMessage
                {...messages.walletBalanceCardSetup}
                values={{
                  link: (chunks) => (
                    <WalletRegistrationOrUpdateButton modalProps={modalProps} trackingId={sourceString}>
                      {({ openModal: openWalletSetupModal }) => (
                        <Button onClick={openWalletSetupModal} theme='link'>
                          {chunks}
                        </Button>
                      )}
                    </WalletRegistrationOrUpdateButton>
                  ),
                }}
              />
            </div>
          </div>
        )}
        {isWalletRegistered && (
          <AddFundsButtonsWithModal
            currencyCode={sellerCurrencyCode}
            currentBalance={currentBalance}
            fundActionPromise={addFundsToWallet}
            onFundConfirmTrack={trackFundEvent}
            onFundError={onFundsError}
            onFundSuccess={onFundSuccess}
            sourceString={sourceString}
          />
        )}
      </div>
      {autoFundModalIsOpen && (
        <AutoFundingModal
          autoFundConfig={autoFundConfig}
          onClose={closeAutoFundModal}
          onSaveAutoFundConfig={onSaveAutoFundConfig}
          onSuccess={onAutoFundSaveSuccess}
          sellerCurrencyCode={sellerCurrencyCode}
          walletId={walletId}
        />
      )}
    </>
  );
};

WalletBalanceCard.propTypes = {
  walletId: PropTypes.string.isRequired,
};
